import { useEffect, useMemo, useState } from 'react';
import { api, fmtCRC } from '../api';
import Modal from '../components/Modal';
import { IconX } from '../components/Icons';
import PlatformVisual, { PLATFORM_ICON_OPTIONS } from '../components/PlatformVisual';

const emptyP = {
  name: '',
  color: '#e50914',
  visualMode: 'icon',
  icon: 'netflix',
  profileEnabled: true,
  profilePrice: '',
  profileCost: '',
  accountEnabled: false,
  accountPrice: '',
  accountCost: '',
  notes: '',
};
const emptyC = {
  name: '',
  platformIds: [],
  profileEnabled: true,
  profilePrice: '',
  profileCost: '',
  accountEnabled: false,
  accountPrice: '',
  accountCost: '',
};

// Bloque de precios reutilizable (perfil / cuenta completa)
function ModeFields({ form, setForm }) {
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => (e) => setForm({ ...form, [k]: e.target.checked });
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-800 p-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={form.profileEnabled} onChange={toggle('profileEnabled')} />
          Vender por perfil
        </label>
        {form.profileEnabled && (
          <div className="mt-2 flex gap-2">
            <input className="input" type="number" min="0" placeholder="Precio venta (₡)" value={form.profilePrice} onChange={upd('profilePrice')} />
            <input className="input" type="number" min="0" placeholder="Costo (₡, opcional)" value={form.profileCost} onChange={upd('profileCost')} />
          </div>
        )}
      </div>
      <div className="rounded-lg border border-slate-800 p-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={form.accountEnabled} onChange={toggle('accountEnabled')} />
          Vender por cuenta completa
        </label>
        {form.accountEnabled && (
          <div className="mt-2 flex gap-2">
            <input className="input" type="number" min="0" placeholder="Precio venta (₡)" value={form.accountPrice} onChange={upd('accountPrice')} />
            <input className="input" type="number" min="0" placeholder="Costo (₡, opcional)" value={form.accountCost} onChange={upd('accountCost')} />
          </div>
        )}
      </div>
    </div>
  );
}

// Muestra precios de un item separando perfil y cuenta completa
function PriceRows({ item }) {
  const profile = item.profileEnabled ? (item.profilePrice ?? item.price) : null;
  const account = item.accountEnabled ? item.accountPrice : null;
  return (
    <div className="mt-2 space-y-1">
      {profile != null && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">Perfil</span>
          <span className="font-semibold text-indigo-300">{fmtCRC(profile)}/mes</span>
        </div>
      )}
      {profile != null && account != null && <div className="border-t border-slate-800/70" />}
      {account != null && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">Cuenta completa</span>
          <span className="font-semibold text-emerald-300">{fmtCRC(account)}/mes</span>
        </div>
      )}
    </div>
  );
}

export default function Catalog() {
  const [platforms, setPlatforms] = useState([]);
  const [combos, setCombos] = useState([]);
  const [q, setQ] = useState('');
  const [pModal, setPModal] = useState(false);
  const [cModal, setCModal] = useState(false);
  const [editP, setEditP] = useState(null);
  const [editC, setEditC] = useState(null);
  const [pForm, setPForm] = useState(emptyP);
  const [cForm, setCForm] = useState(emptyC);
  const [error, setError] = useState('');

  const load = () => {
    api('/platforms').then(setPlatforms).catch(() => {});
    api('/combos').then(setCombos).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const s = q.trim().toLowerCase();
  const shownPlatforms = useMemo(() => platforms.filter((p) => p.name.toLowerCase().includes(s)), [platforms, s]);
  const shownCombos = useMemo(() => combos.filter((c) => c.name.toLowerCase().includes(s)), [combos, s]);

  const fromItem = (it) => ({
    profileEnabled: it.profileEnabled ?? true,
    profilePrice: it.profilePrice ?? it.price ?? '',
    profileCost: it.profileCost ?? it.cost ?? '',
    accountEnabled: it.accountEnabled ?? false,
    accountPrice: it.accountPrice ?? '',
    accountCost: it.accountCost ?? '',
  });

  const openP = (p) => {
    setEditP(p || null);
    setPForm(
      p
        ? {
            name: p.name,
            color: p.color,
            visualMode: p.visualMode || 'color',
            icon: p.icon || 'other',
            notes: p.notes || '',
            ...fromItem(p),
          }
        : emptyP
    );
    setError('');
    setPModal(true);
  };
  const openC = (c) => {
    setEditC(c || null);
    setCForm(c ? { name: c.name, platformIds: c.platformIds.map((p) => p._id), ...fromItem(c) } : emptyC);
    setError('');
    setCModal(true);
  };

  const nums = (f) => ({
    profileEnabled: f.profileEnabled,
    accountEnabled: f.accountEnabled,
    profilePrice: Number(f.profilePrice || 0),
    profileCost: Number(f.profileCost || 0),
    accountPrice: Number(f.accountPrice || 0),
    accountCost: Number(f.accountCost || 0),
  });

  const saveP = async (e) => {
    e.preventDefault();
    try {
      const body = {
        name: pForm.name,
        color: pForm.color,
        visualMode: pForm.visualMode,
        icon: pForm.visualMode === 'icon' ? pForm.icon : '',
        notes: pForm.notes,
        ...nums(pForm),
      };
      if (editP) await api(`/platforms/${editP._id}`, { method: 'PUT', body });
      else await api('/platforms', { method: 'POST', body });
      setPModal(false);
      load();
    } catch (err) { setError(err.message); }
  };

  const saveC = async (e) => {
    e.preventDefault();
    try {
      const body = { name: cForm.name, platformIds: cForm.platformIds, ...nums(cForm) };
      if (editC) await api(`/combos/${editC._id}`, { method: 'PUT', body });
      else await api('/combos', { method: 'POST', body });
      setCModal(false);
      load();
    } catch (err) { setError(err.message); }
  };

  const removeP = async (p) => {
    if (!confirm(`¿Eliminar ${p.name}?`)) return;
    await api(`/platforms/${p._id}`, { method: 'DELETE' });
    load();
  };
  const removeC = async (c) => {
    if (!confirm(`¿Eliminar el combo ${c.name}?`)) return;
    await api(`/combos/${c._id}`, { method: 'DELETE' });
    load();
  };

  const toggleComboPlatform = (id) =>
    setCForm((f) => ({
      ...f,
      platformIds: f.platformIds.includes(id) ? f.platformIds.filter((x) => x !== id) : [...f.platformIds, id],
    }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Catálogo</h1>
        <input className="input max-w-xs" placeholder="Buscar plataforma o combo…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Plataformas</h2>
          <button className="btn-primary" onClick={() => openP()}>+ Plataforma</button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shownPlatforms.map((p) => (
            <div key={p._id} className="card catalog-platform-card">
              <div className="flex items-center gap-3">
                <PlatformVisual platform={p} size="lg" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{p.name}</div>
                </div>
                <button className="text-xs text-slate-400 hover:text-indigo-300" onClick={() => openP(p)}>Editar</button>
                <button className="text-slate-400 hover:text-red-400" title="Eliminar" onClick={() => removeP(p)}><IconX size={16} /></button>
              </div>
              <PriceRows item={p} />
            </div>
          ))}
          {shownPlatforms.length === 0 && <p className="text-slate-500">No hay plataformas. Agrega Netflix, Disney+, Max…</p>}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Combos</h2>
          <button className="btn-primary" onClick={() => openC()} disabled={platforms.length < 2}>+ Combo</button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shownCombos.map((c) => (
            <div key={c._id} className="card catalog-combo-card">
              <div className="flex items-start justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="combo-visual-stack">
                    {c.platformIds.slice(0, 3).map((p) => <PlatformVisual key={p._id} platform={p} size="sm" />)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium">{c.name}</div>
                    <div className="truncate text-sm text-slate-400">{c.platformIds.map((p) => p.name).join(' + ')}</div>
                  </div>
                </div>
                <div className="text-right">
                  <button className="block text-xs text-slate-400 hover:text-indigo-300" onClick={() => openC(c)}>Editar</button>
                  <button className="block text-xs text-slate-400 hover:text-red-400" onClick={() => removeC(c)}>Eliminar</button>
                </div>
              </div>
              <PriceRows item={c} />
            </div>
          ))}
          {shownCombos.length === 0 && <p className="text-slate-500">Crea combos (ej: Netflix + Disney+) con precio especial.</p>}
        </div>
      </section>

      <Modal
        open={pModal}
        title={editP ? 'Editar plataforma' : 'Nueva plataforma'}
        subtitle="Elige cómo quieres identificarla en toda la aplicación."
        size="lg"
        onClose={() => setPModal(false)}
      >
        <form onSubmit={saveP} className="space-y-3">
          <input className="input" placeholder="Nombre (ej: Netflix)" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} required />

          <div className="platform-identity-builder">
            <div className="platform-identity-head">
              <div>
                <strong>Identidad visual</strong>
                <p>Usa un icono reconocido o una ficha de color personalizada.</p>
              </div>
              <div className="platform-mode-switch">
                <button type="button" className={pForm.visualMode === 'icon' ? 'is-active' : ''} onClick={() => setPForm({ ...pForm, visualMode: 'icon' })}>Icono</button>
                <button type="button" className={pForm.visualMode === 'color' ? 'is-active' : ''} onClick={() => setPForm({ ...pForm, visualMode: 'color' })}>Color</button>
              </div>
            </div>

            {pForm.visualMode === 'icon' ? (
              <div className="platform-icon-picker">
                {PLATFORM_ICON_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.id}
                    className={pForm.icon === option.id ? 'is-selected' : ''}
                    onClick={() => setPForm({ ...pForm, icon: option.id, name: pForm.name || option.label })}
                  >
                    <PlatformVisual platform={{ visualMode: 'icon', icon: option.id }} size="lg" />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="platform-color-picker">
                <PlatformVisual platform={{ visualMode: 'color', color: pForm.color }} size="xl" />
                <div>
                  <strong>Color personalizado</strong>
                  <p>Se usará como identificador en catálogo, banco y renovaciones.</p>
                </div>
                <input type="color" value={pForm.color} onChange={(e) => setPForm({ ...pForm, color: e.target.value })} aria-label="Color de la plataforma" />
              </div>
            )}
          </div>
          <ModeFields form={pForm} setForm={setPForm} />
          <textarea className="input" rows="2" placeholder="Notas (opcional)" value={pForm.notes} onChange={(e) => setPForm({ ...pForm, notes: e.target.value })} />
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button className="btn-primary w-full">{editP ? 'Guardar' : 'Crear'}</button>
        </form>
      </Modal>

      <Modal open={cModal} title={editC ? 'Editar combo' : 'Nuevo combo'} onClose={() => setCModal(false)}>
        <form onSubmit={saveC} className="space-y-3">
          <input className="input" placeholder="Nombre del combo" value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} required />
          <div className="space-y-1">
            <p className="text-sm text-slate-400">Plataformas incluidas:</p>
            {platforms.map((p) => (
              <label key={p._id} className="flex cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" checked={cForm.platformIds.includes(p._id)} onChange={() => toggleComboPlatform(p._id)} />
                {p.name}
              </label>
            ))}
          </div>
          <ModeFields form={cForm} setForm={setCForm} />
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button className="btn-primary w-full">{editC ? 'Guardar' : 'Crear combo'}</button>
        </form>
      </Modal>
    </div>
  );
}
